/**
 * Table row for a sport club.
 */
import * as React from 'react';
import { Trophy, MapPin, Globe, Mail } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import type { SportClubPageActions } from './sport-club-configs';
import { deriveSportClubDisplay } from './sport-club-table-utils';
import type { SportClub } from '@/types/sport-club.types';

/** Props for {@link SportClubRow}. */
interface SportClubRowProps {
  club: SportClub;
  pageActions?: SportClubPageActions;
  onView?: (club: SportClub) => void;
  onEdit?: (club: SportClub) => void;
  onDelete?: (id: string) => void;
}

/** Table row for a sport club. */
export const SportClubRow: React.FC<SportClubRowProps> = ({
  club,
  pageActions,
  onView,
  onEdit,
  onDelete,
}) => {
  const canView = pageActions?.canView ?? true;
  const canEdit = pageActions?.canEdit ?? true;
  const canDelete = pageActions?.canDelete ?? true;

  const { typeConfig, TypeIcon, websiteLabel } = deriveSportClubDisplay(club);

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Trophy className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-foreground truncate">{club.name}</span>
            {club.description && (
              <span className="text-xs text-muted-foreground truncate max-w-xs">
                {club.description}
              </span>
            )}
          </div>
        </div>
      </td>

      <td className="text-center">
        {club.sportType ? (
          <LabelBadge
            label={typeConfig.label}
            icon={TypeIcon}
            variant={typeConfig.badgeVariant}
            color={typeConfig.badgeColor}
            size="sm"
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td>
        {club.city ? (
          <div className="flex items-center gap-1.5 text-sm text-foreground">
            <MapPin className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <span>{club.city}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td>
        {club.website ? (
          <div className="flex items-center gap-1.5 text-sm">
            <Globe className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <a
              href={club.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-3-nuance-1)] hover:underline truncate max-w-[160px]"
              onClick={(e) => e.stopPropagation()}
            >
              {websiteLabel}
            </a>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td>
        {club.contactEmail ? (
          <div className="flex items-center gap-1.5 text-sm">
            <Mail className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <a
              href={`mailto:${club.contactEmail}`}
              className="text-foreground hover:text-[var(--color-3-nuance-1)] truncate max-w-[180px]"
              onClick={(e) => e.stopPropagation()}
            >
              {club.contactEmail}
            </a>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          {canView && onView && <Button preset="row-view" onClick={() => onView(club)} />}
          {canEdit && onEdit && <Button preset="row-edit" onClick={() => onEdit(club)} />}
          {canDelete && onDelete && (
            <Button preset="row-delete" onClick={() => onDelete(club.id)} />
          )}
        </div>
      </td>
    </TableRow>
  );
};
